import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export abstract class ServiceApiModel {
    constructor(protected http: HttpClient) {}

    public makeHttpRequest<T = any>(requestOptions: RequestOptions) {
        return this.http
            .request<T>(requestOptions.method, requestOptions.url, requestOptions)
            .toPromise()
            .catch((error: HttpErrorResponse) => Promise.reject(this.handleError(error)));
    }

    public makeHttpObservableRequest<T = any>(requestOptions: RequestOptions): Observable<T> {
        return this.http.request<T>(requestOptions.method, requestOptions.url, requestOptions).pipe(
            catchError((error) => {
                const compatibleError = this.handleErrorObservable(error);
                return throwError(() => compatibleError);
            })
        );
    }

    public makeHttpObservableBlobRequest(requestOptions: RequestOptions): Observable<any> {
        return this.http
            .request(requestOptions.method, requestOptions.url, {
                ...requestOptions,
                responseType: 'blob',
                observe: 'response'
            })
            .pipe(
                catchError((error) => {
                    const compatibleError = this.handleErrorObservable(error);
                    return throwError(() => compatibleError);
                })
            );
    }

    private handleErrorObservable(error: HttpErrorResponse | (() => HttpErrorResponse)) {
        const actualError = typeof error === 'function' ? error() : error;
        return this.handleError(actualError);
    }

    private handleError(responseError: HttpErrorResponse | (() => HttpErrorResponse)): CompatibleHttpErrorResponse {
        const compatibleError = this.normalizeError(responseError);
        const isClientSideError = compatibleError.error instanceof ErrorEvent;

        const errorMessage = this.extractErrorDetails(compatibleError, isClientSideError);

        if (!isClientSideError) {
            compatibleError.data = compatibleError.error || {};
        }

        console.error(errorMessage);
        return compatibleError;
    }

    private normalizeError(responseError: HttpErrorResponse | (() => HttpErrorResponse)): CompatibleHttpErrorResponse {
        let compatibleError: CompatibleHttpErrorResponse;

        if (typeof responseError === 'function') {
            // Caused due to additional error handling in global-unauthorized.interceptor.ts
            compatibleError = responseError() as CompatibleHttpErrorResponse;
        } else {
            compatibleError = responseError as CompatibleHttpErrorResponse;
        }

        return compatibleError;
    }

    private extractErrorDetails(compatibleError: CompatibleHttpErrorResponse, isClientSideError: boolean): string {
        const { error, status, statusText } = compatibleError;

        if (isClientSideError) {
            return `An error occurred: ${error.message}`;
        }

        const isValidError = error && typeof error === 'object';
        const errorDetails = isValidError ? error.message || JSON.stringify(error) : '';

        return `${status} - ${statusText || ''} ${errorDetails}`;
    }
}

export interface RequestOptions {
    url: string;
    method: string;
    body?: any;
    headers?:
        | HttpHeaders
        | {
              [header: string]: string | string[];
          };
    params?:
        | HttpParams
        | {
              [param: string]: any;
          };
    withCredentials?: boolean;
}

export interface CompatibleHttpErrorResponse extends HttpErrorResponse {
    data: any;
}

export type Page<T = any> = {
    totalElements: number;
    offset: number;
    limit: number;
    data: T[];
};
